import { ColumnDef } from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ChevronDownIcon, ChevronsUpDown, Loader2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery } from 'react-query'

import { yupResolver } from '@hookform/resolvers/yup'
import { format } from 'date-fns'
import { toast } from 'sonner'
import { useMediaQuery } from 'usehooks-ts'
import * as yup from 'yup'

import { EditIcon, FlashIcon, TaskSquareIcon, TrashIcon } from '@/assets/icons'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { DataTable, DataTableColumnHeader } from '@/components/ui/data-table'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form'
import { PaginationComponent } from '@/components/ui/pagination'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
	ResponsiveModal,
	ResponsiveModalContent,
	ResponsiveModalDescription,
	ResponsiveModalHeader,
	ResponsiveModalTitle,
} from '@/components/ui/responsive-dialog'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import holidayService from '@/services/holiday-service'

export const HolidaysTable = () => {
	const isSmallScreen = useMediaQuery('(max-width: 64rem)')

	const [page, setPage] = useState(1)
	const [rowsPerPage, setRowsPerPage] = useState(50)
	const [sortColumn, setSortColumn] = useState('')
	const [sortDirection, setSortDirection] = useState('')
	const [openCalendar, setOpenCalendar] = useState(false)
	const [isCreateDialogVisible, setIsCreateDialogVisible] = useState<{
		visible: boolean
		id: Holiday['id'] | null
	}>({
		visible: false,
		id: null,
	})

	const schema = yup.object<{ holiday_date: Date }>().shape({
		holiday_date: yup.date().required('Feiertagsdatum ist erforderlich!'),
	})

	const form = useForm<{ holiday_date: Date }>({
		resolver: yupResolver(schema),
		defaultValues: { holiday_date: null as any },
		mode: 'all',
	})

	const { data, refetch, isLoading } = useQuery(
		'fetch-holidays',
		() =>
			holidayService.fetchHolidaysData({
				pageNumber: page.toString(),
				pageSize: rowsPerPage.toString(),
			}),
		{
			refetchOnWindowFocus: false,
			onError: () => {
				toast.error('Error fetching holidays')
			},
		},
	)

	const { mutate, isLoading: creatingHoliday } = useMutation({
		mutationKey: 'create-holiday',
		mutationFn: (data: { holiday_date: Date }) => holidayService.createHoliday(data),
	})

	const { mutate: updateHoliday, isLoading: updatingHoliday } = useMutation({
		mutationKey: 'update-holiday',
		mutationFn: ({ id, data }: { id: Holiday['id']; data: { holiday_date: Date } }) =>
			holidayService.updateHoliday(id, data),
	})

	const { mutate: deleteHoliday } = useMutation({
		mutationKey: 'delete-holiday',
		mutationFn: (id: Holiday['id']) => holidayService.deleteHoliday(id),
	})

	useEffect(() => {
		refetch()
	}, [rowsPerPage, page])

	const sortedData = useMemo(() => {
		if (!data) return []
		if (!sortColumn) return data.data

		return [...data.data].sort((a, b) => {
			const aValue = a[sortColumn as keyof Holiday]
			const bValue = b[sortColumn as keyof Holiday]

			if (aValue == null || bValue == null) return 0

			let aVal: any = aValue
			let bVal: any = bValue

			if (aVal instanceof Date) aVal = aVal.getTime()
			if (bVal instanceof Date) bVal = bVal.getTime()

			const aParsed = typeof aVal === 'string' ? Date.parse(aVal) : NaN
			const bParsed = typeof bVal === 'string' ? Date.parse(bVal) : NaN

			if (!isNaN(aParsed) && !isNaN(bParsed)) {
				aVal = aParsed
				bVal = bParsed
			}

			if (typeof aVal === 'number' && typeof bVal === 'number') {
				return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
			}

			if (typeof aVal === 'string' && typeof bVal === 'string') {
				return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
			}

			return 0
		})
	}, [data, sortColumn, sortDirection])

	const handleSort = (column: string) => {
		if (sortColumn === column) {
			setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setSortColumn(column)
			setSortDirection('asc')
		}
	}

	const handleDeleteHoliday = (id: number) => {
		deleteHoliday(id, {
			onSuccess: () => {
				toast.success('Feiertag erfolgreich gelöscht')
				refetch()
			},
			onError: () => {
				toast.error('Der Urlaub konnte nicht gelöscht werden. Bitte versuchen Sie es erneut.')
			},
		})
	}

	const onSubmit = (data: { holiday_date: Date }) => {
		if (isCreateDialogVisible.id) {
			updateHoliday(
				{ id: isCreateDialogVisible.id, data },
				{
					onSuccess: () => {
						toast.success('Feiertag erfolgreich aktualisiert')
					},
					onError: () => {
						toast.error(
							'Der Feiertag konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.',
						)
					},
				},
			)
		} else {
			mutate(data, {
				onSuccess: () => {
					toast.success('Gutschein erfolgreich hinzugefügt')
					form.reset()
					setIsCreateDialogVisible({ visible: false, id: null })
					refetch()
				},
				onError: () => {
					toast.error('Der Gutschein kann nicht hinzugefügt werden. Bitte versuche es erneut.')
				},
			})
		}
	}

	const columns: ColumnDef<Holiday>[] = [
		{
			accessorKey: 'holiday_date',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Feiertag" />,
			cell: ({ row }) => <div>{format(new Date(row.getValue('holiday_date')), 'MM-dd-yyyy')}</div>,
		},
		{
			accessorKey: 'createdAt',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Erstellt am" />,
			cell: ({ row }) => <div>{format(new Date(row.getValue('createdAt')), 'MM-dd-yyyy')}</div>,
		},
		{
			id: 'action-holidays',
			enableSorting: false,
			header: ({ column }) => <DataTableColumnHeader column={column} title="Aktionen" />,
			cell: ({ row }) => (
				<div className="flex items-center justify-center gap-0.5">
					<Tooltip>
						<TooltipTrigger
							onClick={() => {
								form.reset({ holiday_date: new Date(row.original.holiday_date) })
								setIsCreateDialogVisible({ visible: true, id: row.original.id })
							}}
							className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-[33px] flex items-center justify-center rounded-full">
							<EditIcon className="shrink-0 size-4" />
						</TooltipTrigger>
						<TooltipContent side="bottom">
							<p className="text-xs font-medium">Aktualisieren</p>
						</TooltipContent>
					</Tooltip>
					<Tooltip>
						<TooltipTrigger
							onClick={() => handleDeleteHoliday(row.original.id)}
							className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-[33px] flex items-center justify-center rounded-full">
							<TrashIcon className="shrink-0 size-4" />
						</TooltipTrigger>
						<TooltipContent side="bottom">
							<p className="text-xs font-medium">Deaktiviert</p>
						</TooltipContent>
					</Tooltip>
				</div>
			),
		},
	]

	const MobileHolidaysTable = () => {
		return (
			<Table>
				<TableHeader>
					<TableRow className="bg-[#F7F8F880] border-b-0! py-[13px] px-4 rounded-lg">
						<TableHead
							align="center"
							onClick={() => handleSort('holiday_date')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							Feiertag
							{sortColumn === 'holiday_date' ? (
								sortDirection === 'desc' ? (
									<ArrowDown size={12} />
								) : (
									<ArrowUp size={12} />
								)
							) : (
								<ChevronsUpDown size={12} />
							)}
						</TableHead>
						<TableHead
							align="center"
							onClick={() => handleSort('createdAt')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							Erstellt am
							{sortColumn === 'createdAt' ? (
								sortDirection === 'desc' ? (
									<ArrowDown size={12} />
								) : (
									<ArrowUp size={12} />
								)
							) : (
								<ChevronsUpDown size={12} />
							)}
						</TableHead>
						<TableHead
							align="center"
							className="text-center text-muted-foreground text-[10px] font-medium">
							Aktion
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{sortedData?.map((holiday, index) => (
						<TableRow
							key={holiday.id.toString() + index.toString()}
							className="h-14 border-b border-[#F4F4F4]">
							<TableCell align="center" className="text-xs">
								{format(new Date(holiday.holiday_date), 'MM-dd-yyyy')}
							</TableCell>
							<TableCell align="center" className="text-[10px]">
								{format(new Date(holiday.createdAt), 'MM-dd-yyyy')}
							</TableCell>
							<TableCell>
								<div className="flex items-center justify-center gap-1">
									<div
										onClick={() => {
											form.reset({ holiday_date: new Date(holiday.holiday_date) })
											setIsCreateDialogVisible({ visible: true, id: holiday.id })
										}}
										className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-6 flex items-center justify-center rounded-full">
										<EditIcon className="shrink-0 max-lg:size-3" />
									</div>
									<div
										onClick={() => handleDeleteHoliday(holiday.id)}
										className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-6 flex items-center justify-center rounded-full">
										<TrashIcon className="shrink-0 max-lg:size-3" />
									</div>
								</div>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		)
	}

	return (
		<div className="rounded-[20px] bg-white max-lg:p-4 max-lg:pr-3 lg:px-4 lg:py-6 flex flex-col">
			<ResponsiveModal
				open={isCreateDialogVisible.visible}
				onOpenChange={() => {
					form.reset({ holiday_date: null as any })
					setIsCreateDialogVisible({ visible: false, id: null })
				}}>
				<ResponsiveModalContent className="gap-0 !max-w-[450px]">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left">
							Feiertag hinzufügen
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="sr-only">
							Feiertag hinzufügen
						</ResponsiveModalDescription>
					</ResponsiveModalHeader>
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)}>
							<div className="mt-6 lg:mt-10">
								<FormField
									control={form.control}
									name="holiday_date"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Tag auswählen <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<Popover open={openCalendar} onOpenChange={setOpenCalendar}>
												<PopoverTrigger asChild>
													<FormControl>
														<Button
															variant="outline"
															className={cn(
																'w-full hover:bg-white max-lg:text-xs px-4 py-[18px] h-10 lg:h-14 rounded-[12px] justify-between border-input focus-visible:border-ring focus-visible:ring-ring/50 focus:!border-3 focus:!border-secondary/50 focus-visible:ring-[3px] text-left font-normal',
																!field.value && 'text-muted-foreground',
															)}>
															{field.value ? (
																format(field.value, 'dd.MM.yyyy')
															) : (
																<span>dd.mm.yyyy</span>
															)}
															<ChevronDownIcon className="size-4 opacity-50" />
														</Button>
													</FormControl>
												</PopoverTrigger>
												<PopoverContent className="w-auto p-0 border-input" align="start">
													<Calendar
														mode="single"
														defaultMonth={field.value}
														selected={field.value}
														onSelect={val => {
															field.onChange(val)
															setOpenCalendar(false)
														}}
														initialFocus
													/>
												</PopoverContent>
											</Popover>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<Button
								variant="secondary"
								className="mt-20 w-full lg:h-[61px] rounded-[12px] font-extrabold text-white">
								{(creatingHoliday || updatingHoliday) && <Loader2 className="animate-spin" />}
								{creatingHoliday || updatingHoliday ? 'Bitte warten' : 'Speichern'}
							</Button>
						</form>
					</Form>
				</ResponsiveModalContent>
			</ResponsiveModal>
			<div className="flex max-lg:flex-col max-lg:gap-3 lg:items-center justify-between">
				<div className="flex items-center gap-2">
					<div className="lg:hidden rounded-xl border border-[#EEEFF0] size-10 flex items-center justify-center">
						<TaskSquareIcon className="text-secondary" />
					</div>
					<h1 className="text-muted-foreground lg:text-secondary text-xs lg:text-2xl font-medium lg:font-extrabold">
						Feiertage
					</h1>
				</div>
				<Button
					onClick={() => setIsCreateDialogVisible({ visible: true, id: null })}
					variant="outline"
					className="text-secondary max-lg:text-xs max-lg:h-10 font-normal border-secondary rounded-lg lg:rounded-xl">
					<FlashIcon className="size-4 text-secondary lg:size-6 shrink-0" />
					FEIERTAG HINZUFÜGEN
				</Button>
			</div>
			<div className="flex max-lg:mt-5 mt-6 flex-col gap-3 lg:gap-4">
				<div className="grid grid-cols-1 items-center gap-[9px] lg:hidden">
					<Select
						value={rowsPerPage.toString()}
						onValueChange={value => setRowsPerPage(Number(value))}>
						<SelectTrigger className="w-full h-10 text-xs">
							<SelectValue placeholder="zeigen 50" />
						</SelectTrigger>
						<SelectContent>
							{[50, 80, 100, 200, 500].map(pageSize => (
								<SelectItem key={pageSize} value={pageSize.toString()}>
									zeigen {pageSize}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>
			<div className="flex max-lg:hidden items-center justify-between mt-6">
				<div className="flex items-center gap-4">
					<Select
						value={rowsPerPage.toString()}
						onValueChange={value => setRowsPerPage(Number(value))}>
						<SelectTrigger className="w-[127px]">
							<SelectValue placeholder="zeigen 50" />
						</SelectTrigger>
						<SelectContent>
							{[50, 80, 100, 200, 500].map(pageSize => (
								<SelectItem key={pageSize} value={pageSize.toString()}>
									zeigen {pageSize}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className="flex items-center gap-8">
					<PaginationComponent
						currentPage={page}
						totalPages={data?.pagination?.totalPages ?? 1}
						onPageChange={setPage}
					/>
				</div>
			</div>
			<div className="mt-4">
				{isSmallScreen ? (
					<MobileHolidaysTable />
				) : (
					<DataTable columns={columns} data={sortedData ?? []} isLoading={isLoading} />
				)}
			</div>
			<div className="mt-6 lg:mt-16 flex justify-center">
				<PaginationComponent
					currentPage={page}
					totalPages={data?.pagination.totalPages ?? 1}
					onPageChange={setPage}
				/>
			</div>
		</div>
	)
}
